import { useState } from 'react';
import { Button, Flex, Loader, Modal, Progress, ScrollArea, Stack, Text } from '@mantine/core';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import {
	BulkAssignCounterpartiesPreviewRes,
	ProgrammeUserWithPermissionRolesRes,
} from '@dealstack/shared/lib/generated';
import { BulkAssignAction } from '@dealstack/shared/lib/bulkAssignAction';
import {
	BulkAssignCounterpartyJobStatus,
	type BulkAssignCounterpartyJobStatusState,
} from '@dealstack/shared/lib/common/bulkAssignCounterparty/jobStatus';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { BulkAssignRoleSection } from './BulkAssignRoleSection';

type RoleChanges = Record<
	string,
	{
		assignUserId: string | null;
		unassignUserIds: string[];
	}
>;

interface Props {
	preview: BulkAssignCounterpartiesPreviewRes | null;
	isLoadingPreview: boolean;
	programmeUsers: ProgrammeUserWithPermissionRolesRes[];
	onClose: () => void;
	onConfirm: (
		changes: Array<{
			roleName: string;
			roleIds: string[];
			action: BulkAssignAction;
			userId: string;
		}>
	) => void;
	isSubmitting: boolean;
	jobStatus: BulkAssignCounterpartyJobStatusState | null;
}

export const BulkAssignCounterpartyModal = ({
	preview,
	isLoadingPreview,
	programmeUsers,
	onClose,
	onConfirm,
	isSubmitting,
	jobStatus,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const [roleChanges, setRoleChanges] = useState<RoleChanges>({});

	const hasChanges = Object.values(roleChanges).some(
		(c) => c.assignUserId || c.unassignUserIds.length > 0
	);

	const handleAssignChange = (roleName: string, userId: string | null) => {
		setRoleChanges((prev) => ({
			...prev,
			[roleName]: {
				assignUserId: userId,
				unassignUserIds: prev[roleName]?.unassignUserIds ?? [],
			},
		}));
	};

	const handleToggleUnassign = (roleName: string, userId: string) => {
		setRoleChanges((prev) => {
			const current = prev[roleName] ?? { assignUserId: null, unassignUserIds: [] };
			const isMarked = current.unassignUserIds.includes(userId);
			return {
				...prev,
				[roleName]: {
					...current,
					unassignUserIds: isMarked
						? current.unassignUserIds.filter((id) => id !== userId)
						: [...current.unassignUserIds, userId],
				},
			};
		});
	};

	const handleConfirm = () => {
		if (!preview) {
			return;
		}

		const changes: Array<{
			roleName: string;
			roleIds: string[];
			action: BulkAssignAction;
			userId: string;
		}> = [];

		for (const role of preview.roles) {
			const change = roleChanges[role.roleName];
			if (!change) {
				continue;
			}

			// Unassigns first so that slots are freed before assigns run
			for (const userId of change.unassignUserIds) {
				changes.push({
					roleName: role.roleName,
					roleIds: role.roleIds,
					action: BulkAssignAction.UNASSIGN,
					userId,
				});
			}

			if (change.assignUserId) {
				changes.push({
					roleName: role.roleName,
					roleIds: role.roleIds,
					action: BulkAssignAction.ASSIGN,
					userId: change.assignUserId,
				});
			}
		}

		onConfirm(changes);
	};

	const renderProgress = (state: BulkAssignCounterpartyJobStatusState) => {
		const percent =
			state.total > 0 ? Math.min(100, Math.round((state.processed / state.total) * 100)) : 0;

		const isTerminal =
			state.status === BulkAssignCounterpartyJobStatus.COMPLETED ||
			state.status === BulkAssignCounterpartyJobStatus.FAILED;

		const subtitleKey = (() => {
			if (state.status === BulkAssignCounterpartyJobStatus.FAILED) {
				return 'documents_page.bulk_assign_counterparty_modal.progress_failed';
			}
			if (state.status === BulkAssignCounterpartyJobStatus.COMPLETED) {
				return 'documents_page.bulk_assign_counterparty_modal.progress_completed';
			}
			return 'documents_page.bulk_assign_counterparty_modal.progress_in_progress';
		})();

		return (
			<Flex direction="column" gap="md" p="md">
				<Text size="sm">
					{tMipManage(subtitleKey, {
						processed: state.processed,
						total: state.total,
					})}
				</Text>
				<Progress
					value={percent}
					size="lg"
					color={state.status === BulkAssignCounterpartyJobStatus.FAILED ? 'red' : 'blue'}
				/>
				<Text
					size="xs"
					c="dimmed"
					data-testid={TEST_ELEMENT_IDS.BULK_ASSIGN_COUNTERPARTY_PROGRESS_BREAKDOWN}
				>
					{tMipManage(
						'documents_page.bulk_assign_counterparty_modal.progress_breakdown',
						{
							assigned: state.assigned,
							unassigned: state.unassigned,
							skipped: state.skipped,
						}
					)}
				</Text>
				{state.errors.length > 0 && (
					<Stack gap={4}>
						{state.errors.map((err, idx) => (
							<Text key={idx} size="xs" c="red">
								{err}
							</Text>
						))}
					</Stack>
				)}
				<Flex justify="flex-end">
					<Button
						data-testid={TEST_ELEMENT_IDS.BULK_ASSIGN_COUNTERPARTY_CLOSE_BUTTON}
						variant="filled"
						onClick={onClose}
						disabled={!isTerminal}
					>
						{tMipManage('documents_page.bulk_assign_counterparty_modal.close_button')}
					</Button>
				</Flex>
			</Flex>
		);
	};

	const modalTitle = jobStatus
		? tMipManage('documents_page.bulk_assign_counterparty_modal.results_title')
		: tMipManage('documents_page.bulk_assign_counterparty_modal.title');

	const renderContent = () => {
		if (jobStatus) {
			return renderProgress(jobStatus);
		}

		if (isLoadingPreview || !preview) {
			return (
				<Flex justify="center" align="center" p="xl">
					<Loader />
				</Flex>
			);
		}

		return (
			<Flex direction="column" gap="md" p="md">
				<Text size="sm">
					{tMipManage('documents_page.bulk_assign_counterparty_modal.description', {
						count: preview.totalDocuments,
					})}
				</Text>
				<ScrollArea.Autosize mah={400}>
					<Stack gap="lg">
						{preview.roles.map((role) => (
							<BulkAssignRoleSection
								key={role.roleName}
								role={role}
								totalDocuments={preview.totalDocuments}
								programmeUsers={programmeUsers}
								changes={roleChanges[role.roleName]}
								onAssignChange={(userId) =>
									handleAssignChange(role.roleName, userId)
								}
								onToggleUnassign={(userId) =>
									handleToggleUnassign(role.roleName, userId)
								}
							/>
						))}
					</Stack>
				</ScrollArea.Autosize>
				<Flex gap="sm" justify="flex-end">
					<Button variant="subtle" onClick={onClose} disabled={isSubmitting}>
						{tMipManage('documents_page.bulk_assign_counterparty_modal.cancel_button')}
					</Button>
					<Button
						data-testid={TEST_ELEMENT_IDS.BULK_ASSIGN_COUNTERPARTY_CONFIRM_BUTTON}
						variant="filled"
						disabled={!hasChanges || isSubmitting}
						loading={isSubmitting}
						onClick={handleConfirm}
					>
						{tMipManage('documents_page.bulk_assign_counterparty_modal.confirm_button')}
					</Button>
				</Flex>
			</Flex>
		);
	};

	return (
		<Modal.Root opened onClose={onClose} centered size="lg">
			<Modal.Overlay />
			{/* data-testid goes on Content, not Root: Root has no box of its own, so a Playwright
			    visibility check against it never resolves even while the modal is genuinely open. */}
			<Modal.Content data-testid={TEST_ELEMENT_IDS.BULK_ASSIGN_COUNTERPARTY_MODAL}>
				<Modal.Header>
					<Modal.Title>{modalTitle}</Modal.Title>
					<Modal.CloseButton />
				</Modal.Header>
				<Modal.Body>{renderContent()}</Modal.Body>
			</Modal.Content>
		</Modal.Root>
	);
};
